import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

// Components
import DataTable from '../../components/DataTable';
import UserDetails from '../../components/popup/UserDetails';
import { ActionButtons, ResumeButton } from '../../components/ActionButtons';
import AddEditCandidates from './AddEditCandidates';

// Prime-React Components
import { confirmDialog } from 'primereact/confirmdialog';
import { Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';

// Constants
import {
  ACTION_TYPE,
  CANDIDATE_FILTER_KEY,
  CANDIDATE_SOURCE_TYPE,
  CANDIDATE_SOURCE_REFERENCE_CODE,
  DEBOUNCE_TIMEOUT,
  INITIAL_PAGE_NUMBER,
  INITIAL_SIZE,
  TABLE_NAME,
  CALLSTATUS_MAP
} from '../../constants/common';
import { CANDIDATE_COLUMNS } from '../../constants/tableColumns';
import { CALL_STATUS_OPTIONS_FILTER } from '../../constants/dropdownOptions';

// Redux-Actions
import {
  deleteCandidate,
  getCandidateDetails,
  getCandidateList,
  getCandidateNote
} from '../../redux/actions/candidates';
import { candidateDetailsAction } from '../../redux/actions/userDetails';

// Utils
import { debounce } from '../../utils/debounce';
import {
  downloadPdf,
  formatExperience,
  isDarkMode,
  normalizeObject,
  openResumeLink,
  renderHrAvatar,
  toLowerCaseText,
  wordCapitalize
} from '../../utils/common';

// Apis
import { filteredHrListApi } from '../../redux/apis/hr';
import Notes from '../../components/popup/Notes';
import SendJobDescription from '../../components/popup/SendJobDescription';
import CopyToClipboard from '../../components/CopyToClipboard';

const pathBase = `${process.env.REACT_APP_API_ENDPOINT}/public/uploads/resume`;
const profilePathBase = `${process.env.REACT_APP_API_ENDPOINT}/public/uploads/profile`;

const candidateTableStates = {
  searchValue: '',
  pageNumber: INITIAL_PAGE_NUMBER,
  size: INITIAL_SIZE,
  sortBy: 'DateModified',
  orderBy: 'desc',
  minExp: null,
  maxExp: null,
  technology: [],
  filterByHR: [],
  callStatus: null,
  isConfirmedUser: true
};

const Candidates = () => {
  const dispatch = useDispatch();
  const technologyListData = useSelector((state) => state.hrs.interviewTechnologyType);
  const [editCandidateId, setEditCandidateId] = useState(null);
  const { candidates, userDetails } = useSelector((state) => state);
  const [candidate, setCandidate] = useState([]);
  const [candidateForNote, setCandidateForNote] = useState([]);
  const [candidateNameForNote, setCandidateNameForNote] = useState([]);
  const [candidateExport, setCandidateExport] = useState([]);
  const [openSelectJDModal, setOpenSelectJDModal] = useState(false);
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const [hrs, setHrs] = useState([]);
  // const [tableStates, setTableStates] = useState(candidateTableStates);
  const [candidateId, setCandidateId] = useState(null);
  const [candidateDetails, setCandidateDetails] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [showNotesPopup, setShowNotesPopup] = useState(false);
  const [isHoveredImage, setIsHoveredImage] = useState({});
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const navigate = useNavigate();
  const normalizeTableState = (state) => {
    if (!state) return candidateTableStates;

    return {
      ...candidateTableStates,
      ...state,
      technology: Array.isArray(state.technology)
        ? state.technology
        : state.technology
        ? [state.technology]
        : []
    };
  };
  const getSavedFilters = () => {
    try {
      const saved = localStorage.getItem(CANDIDATE_FILTER_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  };
  const [tableStates, setTableStates] = useState(() => {
    const saved = getSavedFilters();
    return saved ? normalizeTableState(saved) : candidateTableStates;
  });
  useEffect(() => {
    const { searchValue, minExp, maxExp, technology, filterByHR, callStatus, isConfirmedUser } =
      tableStates;

    const isFilterApplied =
      searchValue?.trim() !== '' ||
      minExp !== null ||
      maxExp !== null ||
      technology?.length > 0 ||
      filterByHR?.length > 0 ||
      callStatus !== null ||
      isConfirmedUser !== true;

    if (isFilterApplied) {
      localStorage.setItem(CANDIDATE_FILTER_KEY, JSON.stringify(tableStates));
    } else {
      localStorage.removeItem(CANDIDATE_FILTER_KEY);
    }
  }, [tableStates]);

  const handleMouseMove = (event) => {
    setMousePosition({ x: event.clientX, y: event.clientY });
  };

  const onTogglePopup = () => setShowPopup((prevState) => !prevState);
  const onToggleNotePopup = () => setShowNotesPopup((prevState) => !prevState);

  const [visibleRight, setVisibleRight] = useState(false);
  const onToggleSiderbar = (state) => {
    if (typeof state === 'boolean') {
      setVisibleRight(state);
    } else {
      setVisibleRight((prev) => !prev);
    }
  };

  // Experience Year Filter
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    dispatch(getCandidateList(tableStates));
  }, [dispatch, tableStates, candidates?.isDeleted, candidates?.isUpdated, candidates?.isAdded]);

  useEffect(() => {
    const modifiedCandidate = candidates?.data?.map((candidateList, index) => {
      const CandidateName = [candidateList?.FirstName, candidateList?.LastName]
        .filter(Boolean)
        .map((n) => n[0].toUpperCase() + n.slice(1).toLowerCase())
        .join(' ');
      return {
        ...candidateList,
        CandidateName: (
          <div className="flex" style={{ position: 'relative' }}>
            <span
              className="table-view-popup-link"
              onClick={() => handleView(candidateList?.ID)}
              title={`Mobile : ${candidateList.Mobile || '--'}`}>
              {`${wordCapitalize(candidateList.FirstName)} ${wordCapitalize(
                candidateList.LastName
              )}`}
            </span>
            {candidateList?.UserProfile && candidateList?.ID && (
              <div
                onMouseEnter={() => {
                  if (candidateList?.UserProfile && candidateList?.ID) {
                    let hoveredImage = { ...isHoveredImage, [candidateList?.ID]: true };
                    setIsHoveredImage(hoveredImage);
                  }
                }}
                onMouseLeave={() => {
                  let hoveredImage = { ...isHoveredImage, [candidateList?.ID]: false };
                  setIsHoveredImage(hoveredImage);
                }}
                onMouseMove={handleMouseMove}>
                <i
                  onClick={() =>
                    openResumeLink(
                      `${profilePathBase}/${candidateList?.ID}/${candidateList?.UserProfile}`
                    )
                  }
                  className="pi pi-fw pi-eye"
                />
                {isHoveredImage[candidateList?.ID] && (
                  <div
                    className="image-preview"
                    key={index}
                    style={{
                      top: `${mousePosition.y + 10}px`,
                      left: `${mousePosition.x + 10}px`
                    }}>
                    <img
                      src={`${profilePathBase}/${candidateList?.ID}/${candidateList?.UserProfile}`}
                      alt="Preview"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        ),
        Technology: (
          <ul className="technology-list" key={index}>
            {candidateList.UMap && candidateList.UMap.length > 0
              ? candidateList.UMap?.map((tech) => (
                  <li
                    key={tech.MapT.ID}
                    style={{
                      backgroundColor: `${
                        isDarkMode() ? tech?.MapT?.DarkModeColor : tech?.MapT?.TechnologyColor
                      }`
                    }}
                    className="technology-badge">
                    {tech.MapT.Technology}
                  </li>
                ))
              : []}
          </ul>
        ),
        Experience: formatExperience(
          candidateList.YearsOfExperience,
          candidateList?.MonthsOfExperience
        ),
        Mobile: (
          <div className="center">
            <span>{candidateList.Mobile || '--'}</span>
            {candidateList.Mobile && <CopyToClipboard text={candidateList.Mobile} />}
          </div>
        ),
        Resume: (
          <div className="flex">
            {candidateList?.ResumeOfCandidate && (
              <>
                <ResumeButton
                  onClick={() => {
                    openResumeLink(
                      `${pathBase}/${candidateList?.ID}/${candidateList?.ResumeOfCandidate?.FilePath}`
                    );
                  }}
                />
                <i
                  data-tip="Download"
                  onClick={() =>
                    downloadPdf(
                      pathBase,
                      candidateList?.ID,
                      candidateList?.ResumeOfCandidate?.FilePath
                    )
                  }
                  className="pi pi-fw pi-download"
                />
              </>
            )}
          </div>
        ),
        InterviewOfCandidate: (
          <Chip
            color={candidateList.InterviewOfCandidate?.length ? 'success' : 'error'}
            label={candidateList.InterviewOfCandidate?.length ? 'Yes' : 'No'}
            clickable
            style={{
              cursor: candidateList.InterviewOfCandidate?.length ? 'pointer' : 'not-allowed'
            }}
            onClick={() => {
              if (candidateList.InterviewOfCandidate?.length) {
                const interviewId = candidateList.InterviewOfCandidate[0]?.ID;

                if (interviewId) {
                  navigate(`/hr/interviews/${interviewId}`);
                }
              }
            }}
          />
        ),
        HR: renderHrAvatar(
          candidateList?.StatusofCallUpdatedByUser?.ID,
          candidateList?.StatusofCallUpdatedByUser?.UserProfile,
          candidateList?.StatusofCallUpdatedByUser?.FirstName,
          candidateList?.StatusofCallUpdatedByUser?.LastName
        ),
        Location: candidateList?.Address || '--',
        Company: candidateList?.CurrentCompany || '--',
        CallStatus: (
          <div className="flex align-items-center">
            <span className="ml-2">{CALLSTATUS_MAP[candidateList?.CallStatus] || '--'}</span>
          </div>
        ),
        action: (
          <div className="flex align-items-center">
            <ActionButtons
              notes
              sendJD
              delete
              onClick={(actionType) =>
                handleRowActions(actionType, candidateList?.ID, CandidateName)
              }
            />
          </div>
        )
      };
    });
    setCandidate(modifiedCandidate);
  }, [candidates, isHoveredImage, isDarkMode()]);

  useEffect(() => {
    const modifiedCandidate = candidates?.data?.map((candidateList, index) => {
      const name = `${wordCapitalize(candidateList.FirstName)} ${wordCapitalize(
        candidateList.LastName
      )}`;
      return {
        CandidateName: name,
        Technology: (
          <ul className="technology-list" key={index}>
            {candidateList.UMap && candidateList.UMap.length > 0
              ? candidateList.UMap?.map((tech) => (
                  <li
                    key={tech.MapT.ID}
                    style={{
                      backgroundColor: `${
                        isDarkMode() ? tech?.MapT?.DarkModeColor : tech?.MapT?.TechnologyColor
                      }`
                    }}
                    className={
                      `${toLowerCaseText(tech.MapT.Technology.replace(/([^\w]+|\s+)/g, ''))}` +
                      '-badge technology-badge'
                    }>
                    {tech.MapT.Technology}
                  </li>
                ))
              : []}
          </ul>
        ),
        Experience: formatExperience(
          candidateList.YearsOfExperience,
          candidateList?.MonthsOfExperience
        ),
        Mobile: (
          <div className="center">
            <span>{candidateList.Mobile || '--'}</span>
            {candidateList.Mobile && <CopyToClipboard text={candidateList.Mobile} />}
          </div>
        )
      };
    });
    setCandidateExport(modifiedCandidate);
  }, [candidates, isDarkMode()]);

  useEffect(() => {
    candidateId && dispatch(candidateDetailsAction(candidateId));
  }, [dispatch, candidateId, userDetails?.isCandidateUpdated]);

  useEffect(() => {
    filteredHrListApi().then((res) => {
      if (res?.data?.data?.length) {
        const sortedHrs = [...res.data.data].sort((a, b) => {
          if (a.isActive === b.isActive) return 0;
          return a.isActive ? -1 : 1;
        });

        const hrs = sortedHrs.map((hr) => ({
          name: `${hr?.FirstName || ''} ${hr?.LastName || ''}${hr?.isActive ? '' : ' (Inactive)'}`,
          id: hr?.ID,
          isActive: hr?.isActive
        }));
        setHrs(hrs);
      }
    });
  }, []);

  const modifyUserDetails = useCallback(() => {
    if (userDetails?.ID) {
      // Display -- if the candidate mobile number is not provided.
      let mobileValue = '--';
      if (userDetails?.Mobile) {
        mobileValue = userDetails?.Mobile;
      }
      if (mobileValue && mobileValue === 'null') {
        mobileValue = '--';
      }
      const candidateDetailsData = [
        {
          title: 'Name',
          value: `${wordCapitalize(userDetails?.FirstName) || ''} 
              ${wordCapitalize(userDetails?.LastName) || ''}`
        },
        {
          title: 'Gender',
          value: userDetails?.Gender == 1 ? 'Male' : userDetails?.Gender == 2 ? 'Female' : '--'
        },
        {
          title: 'Experience',
          value: formatExperience(userDetails?.YearsOfExperience, userDetails?.MonthsOfExperience)
        },
        {
          title: 'Email Address',
          value: (
            <div className="center">
              <span>{userDetails?.Email || '--'}</span>
              {userDetails?.Email && <CopyToClipboard text={userDetails?.Email} />}
            </div>
          )
        },
        {
          title: 'Mobile',
          value: (
            <div className="center">
              <span>{mobileValue || '--'}</span>
              {userDetails?.Mobile && <CopyToClipboard text={mobileValue} />}
            </div>
          )
        },
        {
          title: 'Second Mobile',
          value: (
            <div className="center">
              <span>{userDetails?.SecondaryMobile || '--'}</span>
              {userDetails?.SecondaryMobile && (
                <CopyToClipboard text={userDetails?.SecondaryMobile} />
              )}
            </div>
          )
        },
        {
          title: 'Current Company',
          value: userDetails?.CurrentCompany || ''
        },
        {
          title: 'Technology',
          value:
            userDetails.CandidateTechnology && userDetails.CandidateTechnology.length > 0
              ? userDetails.CandidateTechnology?.map((tech) => tech.Technology).join(', ')
              : ''
        },
        {
          title: 'Current CTC',
          value: userDetails?.CurrentCTC ? `${userDetails?.CurrentCTC} LPA` : ''
        },
        {
          title: 'Candidate Location',
          value: userDetails?.Address || ''
        },
        {
          title: 'Candidate Source',
          value: CANDIDATE_SOURCE_TYPE.find((type) => type.code === userDetails?.CandidateSource)
            ?.name
        }
      ];
      // Only add the candidate reference name if candidate provided source is reference.
      if (userDetails?.CandidateSource === CANDIDATE_SOURCE_REFERENCE_CODE) {
        candidateDetailsData.push({
          title: 'Employee Ref Name',
          value: userDetails?.EmployeeReferenceName || ''
        });
      }
      candidateDetailsData.push(
        {
          title: 'Details Update By',
          value: renderHrAvatar(
            userDetails?.ReportsToUser?.ID,
            userDetails?.ReportsToUser?.UserProfile,
            userDetails?.ReportsToUser?.FirstName,
            userDetails?.ReportsToUser?.LastName,
            true
          )
        },
        {
          title: 'Status Updated Time',
          value: (
            <div className="flex align-items-center">
              {renderHrAvatar(
                userDetails?.StatusofCallUpdatedByUser?.ID,
                userDetails?.StatusofCallUpdatedByUser?.UserProfile,
                userDetails?.StatusofCallUpdatedByUser?.FirstName,
                userDetails?.StatusofCallUpdatedByUser?.LastName,
                false
              )}
              <span className="ml-2">
                {CALLSTATUS_MAP[userDetails?.CallStatus] || '--'}
                {` : ${
                  userDetails?.CallStatusHistory?.[0]?.DateCreated
                    ? moment(userDetails.CallStatusHistory[0].DateCreated).format('lll')
                    : userDetails?.DateModified
                    ? moment(userDetails?.DateModified).format('lll')
                    : null
                }`}
              </span>
            </div>
          )
        }
      );
      setCandidateDetails(candidateDetailsData);
    }
  }, [userDetails]);

  useEffect(() => {
    modifyUserDetails();
  }, [userDetails, modifyUserDetails]);

  const handlePagination = (pageNumber, size) =>
    setTableStates((prevState) => {
      return {
        ...prevState,
        pageNumber,
        size
      };
    });

  const handleSearch = useCallback((searchValue) => {
    setTableStates((prevState) => ({
      ...prevState,
      pageNumber: 1,
      searchValue
    }));
  }, []);

  const debouncedSearch = useMemo(() => debounce(handleSearch, DEBOUNCE_TIMEOUT), [handleSearch]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel?.();
    };
  }, [debouncedSearch]);

  const handleSort = (sortBy) => {
    setTableStates((prevState) => {
      return {
        ...prevState,
        sortBy,
        orderBy: prevState.orderBy === 'asc' ? 'desc' : 'asc'
      };
    });
  };

  const handleFilterByHr = (filterByHR) => {
    setTableStates((prevState) => {
      return {
        ...prevState,
        filterByHR
      };
    });
  };

  const handleYearExperienceFilter = (minExp, maxExp) => {
    setTableStates((prevState) => {
      return {
        ...prevState,
        minExp,
        maxExp
      };
    });
    handleClose();
  };

  const handleView = (ID) => {
    if (ID) {
      onTogglePopup();
      setCandidateId(ID);
    }
  };

  const handleRowActions = (actionType, rowId, CandidateName) => {
    if (actionType === ACTION_TYPE.VIEW) {
      onTogglePopup();
      setCandidateId(rowId);
    } else if (actionType === ACTION_TYPE.EDIT) {
      setEditCandidateId(rowId);
      dispatch(getCandidateDetails(rowId));
      document.body.classList.add('sidebar-open');
      onToggleSiderbar(true);
    } else if (actionType === ACTION_TYPE.NOTES) {
      dispatch(getCandidateNote(rowId));
      setCandidateForNote(rowId);
      setCandidateNameForNote(CandidateName);
      onToggleNotePopup();
    } else if (actionType === ACTION_TYPE.DELETE) {
      confirmDialog({
        header: 'Delete',
        message: 'Are you sure you want to delete?',
        icon: 'pi pi-exclamation-triangle',
        accept: () => dispatch(deleteCandidate(rowId))
      });
    } else if (actionType === ACTION_TYPE.SEND_JD) {
      setSelectedCandidateId(rowId);
      setOpenSelectJDModal(true);
    }
  };

  const handleAdd = () => {
    setEditCandidateId(null);
    setVisibleRight(true);
    document.body.classList.add('sidebar-open');
  };

  const handleStatusFilter = (value) => {
    setTableStates((prevState) => {
      return {
        ...prevState,
        technology: value || []
      };
    });
  };

  const handleCallStatusFilter = (value) => {
    setTableStates((prevState) => {
      return {
        ...prevState,
        callStatus: value
      };
    });
  };

  useEffect(() => {
    const totalPages = Math.ceil(candidates.pagination.totalRecords / tableStates.size);

    // If current page is no longer valid → reset to page 1
    if (tableStates.pageNumber > totalPages && totalPages > 0) {
      setTableStates((prev) => ({
        ...prev,
        pageNumber: 1
      }));
    }
  }, [candidates.pagination.totalRecords]);

  return (
    <>
      <UserDetails
        header="Candidate Details"
        data={candidateDetails}
        onHide={onTogglePopup}
        show={showPopup}
        callStatusHistory={userDetails?.CallStatusHistory}
        candidateId={candidateId}
        onStatusUpdate={() => {
          if (candidateId) {
            dispatch(candidateDetailsAction(candidateId));
            dispatch(getCandidateList(tableStates));
          }
        }}
      />
      {showNotesPopup && (
        <Notes
          onHide={onToggleNotePopup}
          candidateId={candidateForNote}
          candidateNameForNote={candidateNameForNote}
          data={candidates.candidateNotes}
          show={showNotesPopup}
        />
      )}
      {openSelectJDModal && (
        <SendJobDescription
          show={openSelectJDModal}
          onHide={() => setOpenSelectJDModal(false)}
          candidateId={selectedCandidateId}
        />
      )}
      <AddEditCandidates
        ID={editCandidateId}
        onHide={() => onToggleSiderbar(false)}
        show={visibleRight}
        handleView={handleView}
      />
      <DataTable
        name={TABLE_NAME.CANDIDATE}
        searchPlaceHolder="Search by Candidate name, Location, Company and Source"
        totalRecords={candidates.pagination.totalRecords}
        searchValue={tableStates.searchValue}
        pageNumber={tableStates.pageNumber}
        pageSize={tableStates.size}
        onPagination={handlePagination}
        onSearch={debouncedSearch}
        onSort={handleSort}
        onAdd={() => handleAdd()}
        data={candidate}
        hrFilter
        hrs={hrs}
        onFilter={handleFilterByHr}
        selectedHrs={tableStates.filterByHR}
        exportData={candidateExport}
        columns={CANDIDATE_COLUMNS}
        experienceFilter
        open={open}
        label={
          tableStates.minExp && tableStates.maxExp
            ? `${tableStates.minExp} - ${tableStates.maxExp} Years`
            : 'Years of Exp'
        }
        onExperienceFilter={handleClick}
        minExp={tableStates.minExp}
        maxExp={tableStates.maxExp}
        handleYearExperienceFilter={handleYearExperienceFilter}
        handleClose={handleClose}
        anchorEl={anchorEl}
        isDropdownMultiSelect
        dropdownPlaceHolder="Select Technology"
        onStatusFilter={handleStatusFilter}
        dropdown={
          technologyListData?.length
            ? technologyListData.map((tech) => ({ name: tech.Technology, value: tech.ID }))
            : []
        }
        dropdownValue={tableStates.technology}
        secondDropdown={CALL_STATUS_OPTIONS_FILTER}
        secondDropdownValue={tableStates.callStatus}
        secondDropdownPlaceHolder="Select Status of call"
        onSecondDropdownChange={handleCallStatusFilter}
        clearFilter={true}
        isClearFilterDisabled={
          JSON.stringify(normalizeObject(candidateTableStates)) ===
          JSON.stringify(normalizeObject(tableStates))
        }
        clearFilterOnClick={() => {
          setTableStates({
            ...candidateTableStates,
            technology: [],
            callStatus: null,
            minExp: null,
            maxExp: null,
            pageNumber: INITIAL_PAGE_NUMBER,
            size: INITIAL_SIZE
          });
          localStorage.removeItem(CANDIDATE_FILTER_KEY);
        }}
      />
    </>
  );
};

export default Candidates;
